import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';

/* Internal Components */
import ProfileImage from './ProfileImage';

import { ApplicationState } from '../store';
import {
  getCurrentUser,
  CurrentUserState,
} from '../store/ducks/currentUser';

import { getCurrentProfile, CurrentProfileState, updateCurrentProfile, Profile } from '../store/ducks/currentProfile';
import { getPreviousProfile, PreviousProfileState, updatePreviousProfile } from '../store/ducks/previousProfile';

import { listProfiles } from '../api/profiles';
import { getCurrentFullUser } from '../api/users';
import { getCurrentFullCommunity } from '../api/communities';
import { getArtist } from '../api/artists';
import { getVenue } from '../api/venues';
import { updateUser } from 'src/store/ducks/users';
import { updateCommunity } from 'src/store/ducks/communities';
import { updateArtist } from 'src/store/ducks/artists';
import FlatList from 'src/flatList/FlatList';
import DrawerButton from 'src/drawer/DrawerButton';
import Item from 'src/item/Item';
import { Modal, Spinner } from 'react-bootstrap';
import MediaObject from 'src/mediaObject/MediaObject';
import Icon, { Icons } from 'src/icon/Icon';
import { ProfileType } from 'src/types/Profile';
import { capitalizeFirstLetter, modelUser } from 'src/store/helpers/users';
import './ProfileSwitcherButton.scss';

export type SupportedProfileTypes = 'user' | 'community' | 'artist' | 'venue';

type OwnProps = {
  render: (onClick: () => void, onDoublePress: () => void) => React.ReactNode;
  supportedProfileTypes?: SupportedProfileTypes[];
  onSwitchProfile?: (profile: ProfileType) => void;
};

type StateProps = {
  currentUser: CurrentUserState['user'];
  currentProfile: CurrentProfileState;
  previousProfile: PreviousProfileState;
};

type DispatchProps = {
  updateCurrentProfile: (profile: ProfileType) => void;
  updatePreviousProfile: (profile: ProfileType) => void;
  updateProfile: (profile: ProfileType) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  profiles: Profile[];
  drawerOpen: boolean;
  modalOpen: boolean;
  transitioningProfile: Profile | null;
};

const ProfileSwitcherButton: React.FC<Props> = ({
  currentUser,
  currentProfile,
  previousProfile,
  updateCurrentProfile,
  updatePreviousProfile,
  updateProfile,
  render,
  supportedProfileTypes = ['user', 'community', 'artist', 'venue'],
  onSwitchProfile,
}) => {
  const currentUserId = get(currentUser, 'id');

  const [profiles, setProfiles] = useState<State['profiles']>([]);
  const [drawerOpen, setDrawerOpen] = useState<State['drawerOpen']>(false);
  const [modalOpen, setModalOpen] = useState<State['modalOpen']>(false);
  const [transitioningProfile, setTransitioningProfile] = useState<State['transitioningProfile']>(null);

  useEffect(() => {
    if (!currentUserId) return;

    const load = async () => {
      const { items } = await listProfiles({ limit: 10 });
      setProfiles(items);
    };

    load();
  }, [currentUserId]);

  useEffect(() => {
    if (transitioningProfile) openDrawer();
  }, [transitioningProfile]);

  // Handlers
  const openDrawer = () => { setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); };
  const openModal = () => { setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); };

  const handleSwitchProfile = async (profile: Profile) => {
    const profileId = get(profile, 'id');
    const profileType = get(profile, 'type');
    let newProfile = profile;

    updatePreviousProfile(currentProfile!);
    setTransitioningProfile(profile); // triggers modal open via useEffect

    if (profileType === 'user') {
      newProfile = await getCurrentFullUser(profileId);
      newProfile = modelUser(newProfile);
    } else if (profileType === 'community') {
      newProfile = await getCurrentFullCommunity(profileId);
    } else if (profileType === 'artist') {
      newProfile = await getArtist(profileId);
    } else if (profileType === 'venue') {
      newProfile = await getVenue(profileId);
    }

    updateProfile(newProfile);
    updateCurrentProfile(newProfile);
    onSwitchProfile && onSwitchProfile(newProfile);

    // await new Promise((res) => setTimeout(res, 600));

    closeDrawer();
    setTransitioningProfile(null);
    closeModal();
  };

  const switchToPreviousProfile = () => {
    if (!previousProfile) return;
    handleSwitchProfile(previousProfile);
  };

  const filteredProfiles = profiles
  .filter((profile) => {
    const profileId = get(profile, 'id');
    const profileType = get(profile, 'type');
    const currentProfileId = get(currentProfile, 'id');
    return profileId !== currentProfileId && supportedProfileTypes.includes(profileType);
  });

  const dataToRender = uniqBy([
    currentProfile,
    currentUser,
    ...filteredProfiles,
    { type: 'newProfile' },
  ].filter(profile => {
    const profileType = get(profile, 'type');

    return Boolean(profile) && (supportedProfileTypes.includes(profileType as any) || profileType === 'newProfile' as any);
  }), 'id');

  return (
    <div className="bm-ProfileSwitcherButton">
      <DrawerButton
        open={drawerOpen}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        render={(onClick) => render(onClick, switchToPreviousProfile)}
        header={(
          <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
            <div>
              <h3 className="text-bold text-white">Switch profile</h3>
            </div>
            <button onClick={closeDrawer} className="btn pr-0">
              <span className="material-icons text-white">close</span>
            </button>
          </div>
        )}
        content={(
          <div className="bm-ProfileSwitcherButton__drawer pt-1">
            <FlatList
              data={dataToRender}
              contentContainerStyle={{ minHeight: '100vh' }}
              renderItem={({ item, index }) => {
                const profileId = get(item, 'id');
                const profileType = get(item, 'type');

                return (
                  <>
                    {profileType === 'newProfile' ? (
                      <Link
                        key={index}
                        to="/profiles/new"
                        className="bm-ProfileSwitcherButton__drawerOption btn"
                        style={{ width: '100%', margin: '1rem 0' }}
                      >
                        <MediaObject
                          text="New creator profile"
                          subtext="Create a community or artist profile."
                          textColor="light"
                          wrap={false}
                          image={(
                            <div
                              className="d-flex justify-content-center align-items-center bg-dark"
                              style={{
                                height: 65,
                                width: 65,
                                aspectRatio: '1',
                                borderRadius: 32.5,
                              }}
                            >
                              <Icon name={Icons.plus} className="bm-Icon--white" size={42} />
                            </div>
                          )}
                        />
                      </Link>
                    ) : (
                      <button
                        key={index}
                        className="bm-ProfileSwitcherButton__drawerOption btn"
                        onClick={() => handleSwitchProfile(item as Profile)}
                        style={{ width: '100%', textAlign: 'left', margin: '1rem 0' }}
                      >
                        <Item
                          item={item}
                          textColor="light"
                          imageSize={65}
                          subtext={profileType === 'user' ? 'Main profile' : capitalizeFirstLetter(profileType)}
                          right={Boolean(profileId === get(transitioningProfile, 'id')) ? <Spinner variant="light" /> : Boolean(profileId === get(currentProfile, 'id', get(currentUser, 'id'))) && (
                            <Icon name={Icons.checkCircle} className="bm-Icon--primary" size={38} solid />
                          )}
                          wrap={false}
                        />
                      </button>
                    )}
                    {Boolean(index <= filteredProfiles.length + 1) && (
                      <div
                        className="mt-1 mb-1"
                        style={{
                          height: 0, backgroundColor: 'rgba(255,255,255,0.25)',
                          paddingTop: 0.25, paddingBottom: 0.25,
                          width: '100%',
                        }}
                      />
                    )}
                  </>
                );
              }}
            />
          </div>
        )}
      />

      {modalOpen && transitioningProfile && (
        <Modal show={true} onClose={() => closeModal()}>
          <div className="profile-switcher-modal">
            <ProfileImage profile={transitioningProfile} imageSize={75} />
            <h4 className="text-bold" style={{ marginTop: '1rem' }}>
              Switching to {get(transitioningProfile, 'name')}...
            </h4>
          </div>
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state: ApplicationState) => ({
  currentUser: getCurrentUser(state),
  currentProfile: getCurrentProfile(state),
  previousProfile: getPreviousProfile(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCurrentProfile: (profile: ProfileType) => { dispatch(updateCurrentProfile(profile)); },
  updatePreviousProfile: (profile: ProfileType) => { dispatch(updatePreviousProfile(profile)); },
  updateProfile: (profile: ProfileType) => {
    const profileType = get(profile, 'type');

    if (profileType === 'user') {
      dispatch(updateUser(profile));
    } else if (profileType === 'community') {
      dispatch(updateCommunity(profile));
    } else if (profileType === 'artist') {
      dispatch(updateArtist(profile));
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSwitcherButton);
